import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

// ---------- Shared UI ----------
function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, children, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3">
      <div className="sm:w-48 shrink-0 text-sm text-slate-600">{label}</div>
      <div className="flex-1">{children}</div>
      {action && <div className="sm:w-28 shrink-0">{action}</div>}
    </div>
  );
}

function formatThousands(val) {
  if (val === "" || val == null) return "";
  return String(val).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// ---------- Field primitives (memo + local state) ----------
const NumericField = React.memo(function NumericField({
  initialValue = "",
  allowDecimal = false,
  placeholder = "Enter number",
  onCommit, // async (_id, valueNumber|null)
  saving = false,
}) {
  const [val, setVal] = useState(String(initialValue ?? ""));
  useEffect(() => {
    // sunucudan gelen değer değişirse bir defalık senkronla
    setVal(String(initialValue ?? ""));
  }, [initialValue]);

  const onChange = useCallback(
    (e) => {
      const raw = e.target.value.replace(/\./g, "");
      const re = allowDecimal ? /^\d*([.,]\d*)?$/ : /^\d*$/;
      if (raw === "" || re.test(raw)) setVal(raw);
    },
    [allowDecimal]
  );

  const commit = useCallback(() => {
    if (!onCommit) return;
    const numberify =
      val === "" ? null : Number(String(val).replace(",", "."));
    onCommit(numberify);
  }, [val, onCommit]);

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        inputMode={allowDecimal ? "decimal" : "numeric"}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
        placeholder={placeholder}
        value={formatThousands(val)}
        onChange={onChange}
      />
      <button
        onClick={commit}
        disabled={saving}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-60"
      >
        {saving ? "Saving…" : "Update"}
      </button>
    </div>
  );
});

const SelectField = React.memo(function SelectField({
  initialValue = "",
  options = [],
  onCommit,
  saving = false,
}) {
  const [val, setVal] = useState(initialValue ?? "");
  useEffect(() => setVal(initialValue ?? ""), [initialValue]);

  return (
    <div className="flex items-center gap-2">
      <select
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
        value={val}
        onChange={(e) => setVal(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      <button
        onClick={() => onCommit?.(val)}
        disabled={saving}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-60"
      >
        {saving ? "Saving…" : "Update"}
      </button>
    </div>
  );
});

const RadioField = React.memo(function RadioField({
  initialValue = "No",
  choices = ["Yes", "No"],
  onCommit,
  saving = false,
}) {
  const [val, setVal] = useState(initialValue ?? "No");
  useEffect(() => setVal(initialValue ?? "No"), [initialValue]);

  return (
    <div className="flex items-center gap-4">
      {choices.map((c) => (
        <label key={c} className="inline-flex items-center gap-2 text-sm">
          <input
            type="radio"
            className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
            value={c}
            checked={val === c}
            onChange={(e) => setVal(e.target.value)}
          />
          <span>{c}</span>
        </label>
      ))}
      <button
        onClick={() => onCommit?.(val)}
        disabled={saving}
        className="ml-2 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-60"
      >
        {saving ? "Saving…" : "Update"}
      </button>
    </div>
  );
});

// ---------- Main component ----------
export default function AccountRules(props) {
  const accountname = props.match?.params?.accountname;
  const platform = props.match?.params?.platform;

  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState({}); // {ruleId: boolean}

  // fetch
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const { data } = await axios.post(
          "https://act-api.vercel.app/findrule",
          { accountname }
        );
        if (!mounted) return;
        setRules(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setLoadError("Kurallar yüklenemedi. Lütfen tekrar deneyin.");
      } finally {
        mounted && setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [accountname]);

  // hızlı erişim
  const byDesc = useMemo(() => {
    const m = {};
    for (const r of rules) m[(r.ruleDescription || "").toLowerCase()] = r;
    return m;
  }, [rules]);

  // görünürlükler
  const isMeta = platform === "Meta";
  const isGoogle = platform === "Google";
  const isLinkedIn = platform === "LinkedIn";

  // rule objeleri
  const budgetRule = byDesc["budget"];
  const adsetRule = byDesc["ad set budget"];
  const budgetTypeRule = byDesc["budget type"];
  const abRule = byDesc["a/b testing"];
  const taxonomyRule = byDesc["taxonomy"];
  const currencyRule = byDesc["currency"];
  const gbmRule =
    byDesc["target gbm budget"] || byDesc["target gbm budget".toLowerCase()];

  // commit helper
  const commit = useCallback(async (_id, value) => {
    if (!_id) return;
    try {
      setSaving((s) => ({ ...s, [_id]: true }));
      setSuccess(false);
      await axios.post("https://act-api.vercel.app/updateRule", {
        _id,
        rule: value,
      });
      setSuccess(true);
    } finally {
      setSaving((s) => ({ ...s, [_id]: false }));
    }
  }, []);

  const TaxonomyOptions = [
    "BrandName|Platform|CampaignName|Objective|StartDate|EndDate",
    "BrandName-Platform-CampaignName-Objective-StartDate-EndDate",
    "BrandName_Platform_CampaignName_Objective_StartDate_EndDate",
  ];
  const BudgetTypeOptions = ["Daily Budget", "Lifetime Budget"];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50 border-b border-slate-200">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold truncate">{accountname}</h1>
            <p className="text-xs text-slate-500 truncate">Account Rules</p>
          </div>
          <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-medium">
            {platform}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 space-y-6">
        {loadError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {loadError}
          </div>
        )}
        {success && (
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
            You have successfully updated the information.
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="h-4 w-40 bg-slate-200 rounded animate-pulse mb-4" />
                <div className="h-10 w-full bg-slate-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Budget & Strategy */}
            {(budgetRule ||
              (isMeta && (adsetRule || budgetTypeRule || abRule)) ||
              (isLinkedIn && (budgetTypeRule || currencyRule)) ||
              (isGoogle && gbmRule)) && (
              <Section title="Budget & Strategy">
                {/* Budget */}
                {budgetRule && (
                  <Row label="Budget">
                    <NumericField
                      key={budgetRule._id}             // STABLE KEY
                      initialValue={budgetRule.rule ?? ""}
                      allowDecimal={false}
                      onCommit={(num) => commit(budgetRule._id, num)}
                      saving={!!saving[budgetRule._id]}
                    />
                  </Row>
                )}

                {/* Ad Set Budget (Meta) */}
                {isMeta && adsetRule && (
                  <Row label="Ad Set Budget">
                    <NumericField
                      key={adsetRule._id}
                      initialValue={adsetRule.rule ?? ""}
                      allowDecimal={false}
                      onCommit={(num) => commit(adsetRule._id, num)}
                      saving={!!saving[adsetRule._id]}
                    />
                  </Row>
                )}

                {/* Budget Type (Meta + LinkedIn) */}
                {(isMeta || isLinkedIn) && budgetTypeRule && (
                  <Row label="Budget Type">
                    <SelectField
                      key={budgetTypeRule._id}
                      initialValue={budgetTypeRule.rule ?? "Daily Budget"}
                      options={BudgetTypeOptions}
                      onCommit={(val) => commit(budgetTypeRule._id, val)}
                      saving={!!saving[budgetTypeRule._id]}
                    />
                  </Row>
                )}

                {/* A/B Testing (Meta) */}
                {isMeta && abRule && (
                  <Row label="A/B Testing">
                    <RadioField
                      key={abRule._id}
                      initialValue={abRule.rule ?? "No"}
                      choices={["Yes", "No"]}
                      onCommit={(val) => commit(abRule._id, val)}
                      saving={!!saving[abRule._id]}
                    />
                  </Row>
                )}

                {/* Currency (LinkedIn) */}
                {isLinkedIn && currencyRule && (
                  <Row label="Currency">
                    <SelectField
                      key={currencyRule._id}
                      initialValue={currencyRule.rule ?? "USD"}
                      options={["USD", "EUR", "TRY", "GBP"]}
                      onCommit={(val) => commit(currencyRule._id, val)}
                      saving={!!saving[currencyRule._id]}
                    />
                  </Row>
                )}

                {/* Target GBM Budget (Google) */}
                {isGoogle && gbmRule && (
                  <Row label="Target GBM Budget">
                    <NumericField
                      key={gbmRule._id}
                      initialValue={gbmRule.rule ?? ""}
                      allowDecimal={false}
                      onCommit={(num) => commit(gbmRule._id, num)}
                      saving={!!saving[gbmRule._id]}
                    />
                  </Row>
                )}
              </Section>
            )}

            {/* Taxonomy */}
            {taxonomyRule && (
              <Section title="Naming Taxonomy">
                <Row label="Pattern">
                  <SelectField
                    key={taxonomyRule._id}
                    initialValue={
                      taxonomyRule.rule ??
                      "BrandName|Platform|CampaignName|Objective|StartDate|EndDate"
                    }
                    options={[
                      "BrandName|Platform|CampaignName|Objective|StartDate|EndDate",
                      "BrandName-Platform-CampaignName-Objective-StartDate-EndDate",
                      "BrandName_Platform_CampaignName_Objective_StartDate_EndDate",
                    ]}
                    onCommit={(val) => commit(taxonomyRule._id, val)}
                    saving={!!saving[taxonomyRule._id]}
                  />
                </Row>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
